import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { THEME } from '../../theme';
import { manualPlacementScore } from './Games/puzzleMasterRun';
import TwoJointPoleScene, { TJP_TRACK_LIMIT } from './visuals/TwoJointPoleScene';
import TwoJointPoleSceneMultiplayer from './visuals/TwoJointPoleSceneMultiplayer';
import RoadFighterScene, { FuelMeter, TrackProgressMeter } from './visuals/RoadFighterScene';
import SpaceInvaderScene, {
    SI_CANVAS_W,
    SI_CANVAS_H,
    SI_PLAYER_SIZE,
    SI_INVADER_SIZE,
    SI_INVADER_ROWS,
    SI_INVADER_COLS,
    SI_PLAYFIELD_SX,
    SI_BULLET_W,
    siInvaderTypeFromRow,
    createInvaderGrid,
    collectSpaceInvaderBullets,
} from './visuals/SpaceInvaderScene';
import {
    drawTetrisBoard,
    TETROMINOES,
} from './visuals/TetrisScene';
import { STACKFALL_DISPLAY_NAME } from './gameNames';
import SantaDropScene, { burstsFromTicks } from './visuals/SantaDropScene';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export const toXY = (pos) => {
    if (!pos) return { x: 0, y: 0 };
    if (Array.isArray(pos)) return { x: pos[1], y: pos[0] };
    return { x: pos.x ?? pos.col ?? 0, y: pos.y ?? pos.row ?? 0 };
};

export const DIR_MAP = {
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const timeline = (run) => run.history || run.spin_history || run.move_history || [];

const toNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const toCount = (value) => Math.max(0, Math.trunc(toNumber(value, 0)));

const formatStatValue = (value) => {
    if (value == null || value === '') return '—';
    if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
    return String(value);
};

function outcomeLabel(...values) {
    const raw = values.find((value) => value != null && value !== '');
    if (!raw) return null;
    return String(raw).replace(/_/g, ' ');
}

function finalScore(run, fallback = null) {
    if (run?.score != null) return run.score;
    if (run?.final_score != null) return run.final_score;
    return fallback;
}

/** Prefer an explicit per-step score on a history entry, else a derived value. */
function stepScoreFromEntry(entry, computed, run, fallback = null) {
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        for (const key of [
            'score', 'current_score', 'bankroll', 'credits_after', 'balance',
            'revealed_safe', 'hits', 'campaign_distance', 'pairs_completed',
        ]) {
            if (entry[key] != null && entry[key] !== '') {
                const n = toNumber(entry[key], null);
                if (n != null) return n;
            }
        }
    }
    if (computed != null) return computed;
    return finalScore(run, fallback);
}

function detectTttWinner(board) {
    for (let i = 0; i < 3; i++) {
        if (board[i][0] && board[i][0] === board[i][1] && board[i][1] === board[i][2]) return board[i][0];
        if (board[0][i] && board[0][i] === board[1][i] && board[1][i] === board[2][i]) return board[0][i];
    }
    if (board[0][0] && board[0][0] === board[1][1] && board[1][1] === board[2][2]) return board[0][0];
    if (board[0][2] && board[0][2] === board[1][1] && board[1][1] === board[2][0]) return board[0][2];
    return null;
}

function tttScoreFromBoard(board) {
    const winner = detectTttWinner(board);
    return winner === 'X' ? 1 : 0;
}

function detectC4Winner(board) {
    const ROWS = board.length;
    const COLS = board[0]?.length || 0;
    const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
    for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
            const p = board[r][c];
            if (!p) continue;
            for (const [dr, dc] of dirs) {
                let count = 1;
                for (let k = 1; k < 4; k++) {
                    const nr = r + dr * k;
                    const nc = c + dc * k;
                    if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS || board[nr][nc] !== p) break;
                    count += 1;
                }
                if (count >= 4) return p;
            }
        }
    }
    return null;
}

function c4ScoreFromBoard(board) {
    const winner = detectC4Winner(board);
    if (!winner) return 0;
    return winner === 'X' || winner === 'R' || winner === 1 ? 1 : 0;
}

function numberlinkPairsCompleted(path, endpoints) {
    const pairs = endpoints || {
        1: [[0, 0], [4, 4]],
        2: [[0, 4], [4, 0]],
        3: [[1, 1], [3, 3]],
        4: [[1, 3], [3, 1]],
    };
    let count = 0;
    Object.entries(pairs).forEach(([pair, pts]) => {
        const endpointSet = new Set(pts.map(([r, c]) => `${r},${c}`));
        const cells = new Set();
        path.forEach((row, r) => row.forEach((cell, c) => {
            if (cell === String(pair)) cells.add(`${r},${c}`);
        }));
        if ([...endpointSet].every((key) => cells.has(key)) && cells.size >= 2) count += 1;
    });
    return count;
}

function cumulativeScore(history = [], step = 0, selector = () => 0) {
    const clamped = Math.max(0, Math.min(toCount(step), history.length));
    let total = 0;
    for (let i = 0; i < clamped; i++) total += toNumber(selector(history[i], i), 0);
    return total;
}

function chipSx(emphasis = false) {
    return emphasis
        ? {
            bgcolor: THEME.primary,
            color: '#111',
            fontWeight: 'bold',
        }
        : {
            color: 'white',
            borderColor: THEME.border,
        };
}

function makeStats(items = []) {
    return items.filter((item) => item && item.value != null && item.value !== '');
}

export function ReplayStatsBar({ stats = [], compact = false, align = 'center' }) {
    if (!stats.length) return null;
    const justifyContent = align === 'end'
        ? 'flex-end'
        : align === 'start'
            ? 'flex-start'
            : 'center';
    return (
        <Stack
            direction="row"
            spacing={compact ? 0.5 : 1}
            justifyContent={justifyContent}
            sx={{ flexWrap: 'wrap', gap: compact ? 0.5 : 1 }}
        >
            {stats.map((stat) => (
                <Chip
                    key={stat.key}
                    size="small"
                    label={`${stat.label} ${formatStatValue(stat.value)}`}
                    variant={stat.emphasis ? 'filled' : 'outlined'}
                    sx={{
                        ...chipSx(stat.emphasis),
                        ...(compact ? {
                            height: 22,
                            fontSize: '0.68rem',
                            '& .MuiChip-label': { px: 0.75 },
                        } : null),
                    }}
                />
            ))}
        </Stack>
    );
}

function looksLikeSnakeSegment(seg) {
    if (Array.isArray(seg) && seg.length >= 2) {
        return Number.isFinite(Number(seg[0])) && Number.isFinite(Number(seg[1]));
    }
    if (!seg || typeof seg !== 'object') return false;
    return [seg.x, seg.y, seg.row, seg.col].some((v) => Number.isFinite(v));
}

export function extractSnakeDir(entry) {
    if (typeof entry === 'string') {
        const key = entry.toLowerCase();
        return DIR_MAP[key] ? key : null;
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const raw = entry.action ?? entry.direction ?? entry.move ?? entry.dir;
    if (typeof raw !== 'string') return null;
    const key = raw.toLowerCase();
    return DIR_MAP[key] ? key : null;
}

export function extractSnakeBody(entry) {
    if (Array.isArray(entry) && entry.length && looksLikeSnakeSegment(entry[0])) {
        return entry.map(toXY);
    }
    if (!entry || typeof entry !== 'object') return null;
    const raw = entry.snake || entry.state?.snake || entry.body;
    if (!Array.isArray(raw) || !raw.length || !looksLikeSnakeSegment(raw[0])) return null;
    return raw.map(toXY);
}

export function extractSnakeFood(entry) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const raw = entry.food ?? entry.state?.food ?? entry.apple ?? entry.food_pos ?? entry.food_position;
    if (raw == null) return null;
    return toXY(raw);
}

/** Prefer per-tick body snapshots; fall back to direction lists (agent move_history). */
export function snakeTimeline(run = {}) {
    const hist = Array.isArray(run.history) ? run.history : [];
    const moves = Array.isArray(run.move_history) ? run.move_history : [];
    if (hist.some(extractSnakeBody)) return hist;
    if (moves.some(extractSnakeBody)) return moves;
    if (moves.length) return moves;
    return hist;
}

/**
 * Snake playback frames are init (step 0) plus one frame per history tick.
 * Steps past the last index wrap to 0 so looping replay never overshoots.
 */
export function wrapSnakeReplayStep(step, historyLength) {
    const len = Math.max(0, Math.trunc(Number(historyLength)) || 0);
    const frames = len + 1;
    if (frames <= 1) return 0;
    const n = Math.trunc(Number(step));
    if (!Number.isFinite(n)) return 0;
    return ((n % frames) + frames) % frames;
}

function inferDirFromHeads(prev, next) {
    if (!prev || !next) return null;
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    if (dx === 1 && dy === 0) return 'right';
    if (dx === -1 && dy === 0) return 'left';
    if (dx === 0 && dy === 1) return 'down';
    if (dx === 0 && dy === -1) return 'up';
    return null;
}

// ---------------------------------------------------------------------------
// Snake (+ direction-aware eyes)
// ---------------------------------------------------------------------------

export function computeSnakeFrame(run, step) {
    const init = run.init_state || {};
    const initialBoardSize = init.board_size || run.board_size || 10;
    const ticks = snakeTimeline(run);
    const rawFoodHistory = run.food_history || [];
    const foodHistory = Array.isArray(rawFoodHistory) ? rawFoodHistory.map(toXY) : [];

    let snake = (init.snake && Array.isArray(init.snake) && init.snake.length)
        ? init.snake.map(toXY)
        : [{ x: Math.floor(initialBoardSize / 2), y: Math.floor(initialBoardSize / 2) }];
    let food = foodHistory.length > 0 ? foodHistory[0] : toXY(init.food || init.food_pos || init.food_position);
    let fhIdx = 0;
    let direction = 'right';

    const wrappedStep = wrapSnakeReplayStep(step, ticks.length);
    for (let i = 0; i < wrappedStep; i++) {
        const entry = ticks[i];
        const body = extractSnakeBody(entry);
        const dir = extractSnakeDir(entry);
        const entryFood = extractSnakeFood(entry);
        if (dir) direction = dir;
        if (entryFood) food = entryFood;

        if (body) {
            // Body snapshot is the pose AFTER this tick. Applying dir on top
            // double-steps the head and the next snapshot snaps it back (oscillation).
            const inferred = inferDirFromHeads(snake[0], body[0]);
            if (!dir && inferred) direction = inferred;
            snake = body;
            continue;
        }

        if (!dir) continue;

        const d = DIR_MAP[dir];
        const head = { x: snake[0].x + d.x, y: snake[0].y + d.y };
        const grew = head.x === food.x && head.y === food.y;
        snake = [head, ...snake];
        if (grew) {
            if (foodHistory.length > 0 && fhIdx + 1 < foodHistory.length) {
                fhIdx += 1;
                food = foodHistory[fhIdx];
            }
        } else {
            snake.pop();
        }
    }

    const baseLength = init.snake ? init.snake.length : 1;
    const score = (snake.length - baseLength) * 10;
    const growthBoardSize = Math.min(10 + (Math.floor(score / 100) * 2), 30);
    const tickBoardSize = wrappedStep > 0
        ? Number(ticks[wrappedStep - 1]?.board_size) || null
        : null;
    // New runs record variable_grid (default false = fixed). Legacy runs without
    // the flag still use the score-based growth heuristic when tick size is missing.
    const variableGridFlag = init.variable_grid ?? run.variable_grid;
    const useGrowthFallback = variableGridFlag !== false;
    const boardSize = tickBoardSize
        || (useGrowthFallback ? growthBoardSize : null)
        || initialBoardSize;
    return {
        snake,
        food,
        boardSize,
        direction,
        score,
        moves: wrappedStep,
        length: snake.length,
        ticks: wrappedStep,
        finalScore: finalScore(run, score),
        variableGrid: Boolean(variableGridFlag),
    };
}

function snakeEyeOffsets(direction, cellPx) {
    const o = cellPx / 4;
    const eyeR = Math.max(2, Math.floor(cellPx / 6));
    // Pair of eyes placed toward the movement direction
    switch (direction) {
        case 'left':
            return { eyes: [{ dx: -o, dy: -o / 2 }, { dx: -o, dy: o / 2 }], eyeR };
        case 'right':
            return { eyes: [{ dx: o, dy: -o / 2 }, { dx: o, dy: o / 2 }], eyeR };
        case 'up':
            return { eyes: [{ dx: -o / 2, dy: -o }, { dx: o / 2, dy: -o }], eyeR };
        case 'down':
            return { eyes: [{ dx: -o / 2, dy: o }, { dx: o / 2, dy: o }], eyeR };
        default:
            return { eyes: [{ dx: -o / 2, dy: -o }, { dx: o / 2, dy: -o }], eyeR };
    }
}

export function SnakeReplayView({ run, step }) {
    const { snake, food, boardSize, direction } = useMemo(() => computeSnakeFrame(run, step), [run, step]);
    const cellPx = Math.min(24, Math.floor(360 / boardSize));
    const size = cellPx * boardSize;
    const { eyes, eyeR } = snakeEyeOffsets(direction, cellPx);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                position: 'relative',
                width: size,
                height: size,
                bgcolor: '#0a0a0a',
                border: `1px solid ${THEME.border}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundImage: `linear-gradient(${THEME.grid} 1px, transparent 1px), linear-gradient(90deg, ${THEME.grid} 1px, transparent 1px)`,
                backgroundSize: `${cellPx}px ${cellPx}px`,
            }}>
                {snake.map((seg, idx) => (
                    <Box key={idx} sx={{
                        position: 'absolute',
                        left: seg.x * cellPx + 1,
                        top: seg.y * cellPx + 1,
                        width: cellPx - 2,
                        height: cellPx - 2,
                        bgcolor: idx === 0 ? THEME.primary : '#1e8e45',
                        borderRadius: 0.5,
                    }}>
                        {idx === 0 && eyes.map((e, ei) => (
                            <Box key={ei} sx={{
                                position: 'absolute',
                                left: cellPx / 2 - 1 + e.dx - eyeR,
                                top: cellPx / 2 - 1 + e.dy - eyeR,
                                width: eyeR * 2,
                                height: eyeR * 2,
                                borderRadius: '50%',
                                bgcolor: '#0a0a0a',
                            }} />
                        ))}
                    </Box>
                ))}
                <Box sx={{
                    position: 'absolute',
                    left: food.x * cellPx + 2,
                    top: food.y * cellPx + 2,
                    width: cellPx - 4,
                    height: cellPx - 4,
                    bgcolor: THEME.accent,
                    borderRadius: 1,
                }} />
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Sliding tiles / Puzzle Master
// ---------------------------------------------------------------------------

export function computePuzzleFrame(run, step) {
    const GRID_SIZE = 4;
    const EMPTY_CELL = null;
    const init = run.init_state;
    let puzzle = Array.isArray(init) ? init.map((v) => (v === 999 ? EMPTY_CELL : v)) : [];
    const history = run.history || [];
    const moves = run.move_history || history || [];

    // GM / agent runs may store full board snapshots per tick.
    if (history.some((h) => h && Array.isArray(h.puzzle))) {
        const clampedSnap = Math.max(0, Math.min(step, history.length));
        if (clampedSnap === 0) {
            puzzle = Array.isArray(init) ? init.map((v) => (v === 999 ? EMPTY_CELL : v)) : puzzle;
        } else {
            const entry = history[clampedSnap - 1] || {};
            if (Array.isArray(entry.puzzle)) {
                puzzle = entry.puzzle.map((v) => (v === 999 ? EMPTY_CELL : v));
            }
        }
        const entry = clampedSnap > 0 ? history[clampedSnap - 1] : null;
        const score = stepScoreFromEntry(entry, manualPlacementScore(puzzle), run);
        return {
            puzzle,
            gridSize: GRID_SIZE,
            moves: clampedSnap,
            tilesPlaced: puzzle.filter((tile) => tile !== EMPTY_CELL).length,
            score,
            finalScore: finalScore(run, score),
        };
    }

    const getEmptyPosition = (p) => p.indexOf(EMPTY_CELL);
    const getAdjacentPositions = (position, gridSize) => {
        const row = Math.floor(position / gridSize);
        const col = position % gridSize;
        const adjacent = [];
        if (row > 0) adjacent.push(position - gridSize);
        if (row < gridSize - 1) adjacent.push(position + gridSize);
        if (col > 0) adjacent.push(position - 1);
        if (col < gridSize - 1) adjacent.push(position + 1);
        return adjacent;
    };

    const clampedStep = Math.max(0, Math.min(step, moves.length));
    for (let i = 0; i < clampedStep; i++) {
        const move = moves[i];
        let movePosition = -1;
        if (typeof move === 'object' && move !== null) {
            if (Number.isInteger(move.to)) movePosition = move.to;
            else {
                const tileVal = Number.isInteger(move.tile)
                    ? move.tile
                    : (Number.isInteger(move.action) ? move.action : null);
                if (tileVal != null) {
                    const tile = tileVal === 999 ? EMPTY_CELL : tileVal;
                    movePosition = puzzle.indexOf(tile);
                }
            }
        } else if (Number.isInteger(move)) {
            const tile = move === 999 ? EMPTY_CELL : move;
            let idx = puzzle.indexOf(tile);
            if (idx === -1 && move >= 0 && move < GRID_SIZE * GRID_SIZE) idx = move;
            movePosition = idx;
        }
        if (movePosition < 0 || movePosition >= GRID_SIZE * GRID_SIZE) continue;
        const emptyPos = getEmptyPosition(puzzle);
        if (!getAdjacentPositions(emptyPos, GRID_SIZE).includes(movePosition)) continue;
        const newPuzzle = [...puzzle];
        [newPuzzle[emptyPos], newPuzzle[movePosition]] = [newPuzzle[movePosition], newPuzzle[emptyPos]];
        puzzle = newPuzzle;
    }

    const entry = clampedStep > 0 ? moves[clampedStep - 1] : null;
    const placementScore = manualPlacementScore(puzzle);
    const score = stepScoreFromEntry(entry, placementScore, run);

    return {
        puzzle,
        gridSize: GRID_SIZE,
        moves: clampedStep,
        tilesPlaced: puzzle.filter((tile) => tile !== EMPTY_CELL).length,
        score,
        finalScore: finalScore(run, score),
    };
}

function getTileColor(number) {
    const colors = [
        '#2e7d32', '#1565c0', '#c62828', '#f9a825',
        '#6a1b9a', '#00838f', '#ad1457', '#283593',
        '#4e342e', '#37474f', '#0277bd', '#ef6c00',
        '#00695c', '#4527a0', '#d84315',
    ];
    return colors[(number - 1) % colors.length] + 'dd';
}

export function PuzzleReplayView({ run, step }) {
    const { puzzle, gridSize } = useMemo(() => computePuzzleFrame(run, step), [run, step]);
    if (!puzzle.length) {
        return <Typography sx={{ color: THEME.secondary, textAlign: 'center' }}>No puzzle state available.</Typography>;
    }
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                gap: 1,
                width: 320,
                height: 320,
            }}>
                {puzzle.map((tile, idx) => (
                    <Box key={idx} sx={{
                        bgcolor: tile ? getTileColor(tile) : 'rgba(255,255,255,0.03)',
                        border: tile ? '1px solid rgba(255,255,255,0.1)' : '2px dashed rgba(255,255,255,0.05)',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.3rem',
                    }}>
                        {tile || ''}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Generic board snapshots
// ---------------------------------------------------------------------------

export function BoardSnapshotReplayView({ run, step }) {
    const snapshots = run.board_snapshots || run.snapshots || [];
    const fromHistory = (!snapshots.length && Array.isArray(run.history))
        ? run.history.map((h) => (h && h.board) || null).filter(Boolean)
        : [];
    const frames = snapshots.length ? snapshots : fromHistory;
    const clampedStep = Math.max(0, Math.min(step, Math.max(frames.length - 1, 0)));
    const board = frames[clampedStep];

    if (!board || !Array.isArray(board)) {
        return <Typography sx={{ color: THEME.secondary, textAlign: 'center' }}>No board snapshot available for this step.</Typography>;
    }

    const cols = Array.isArray(board[0]) ? board[0].length : 1;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 0.5,
                width: Math.min(360, cols * 48),
            }}>
                {board.flat().map((cell, idx) => (
                    <Box key={idx} sx={{
                        aspectRatio: '1 / 1',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border: `1px solid ${THEME.border}`,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: THEME.primary,
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                    }}>
                        {cell === null || cell === undefined || cell === '' || cell === 0 ? '' : String(cell)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Tic-Tac-Toe (grid style matching live play)
// ---------------------------------------------------------------------------

export function computeTttFrame(run, step) {
    const size = 3;
    const board = Array.from({ length: size }, () => Array(size).fill(''));
    const moves = run.move_history || [];
    const clamped = Math.max(0, Math.min(step, moves.length));
    for (let i = 0; i < clamped; i++) {
        const m = moves[i] || {};
        const r = Number.isInteger(m.row) ? m.row : -1;
        const c = Number.isInteger(m.col) ? m.col : -1;
        const p = m.player || (i % 2 === 0 ? 'X' : 'O');
        if (r >= 0 && r < size && c >= 0 && c < size) board[r][c] = p;
    }
    const entry = clamped > 0 ? moves[clamped - 1] : null;
    const derivedScore = tttScoreFromBoard(board);
    const score = stepScoreFromEntry(entry, derivedScore, run);
    return {
        board,
        moves: clamped,
        score,
        finalScore: finalScore(run, score),
        outcome: outcomeLabel(run.outcome, run.result, run.winner, detectTttWinner(board)),
    };
}

export function TttReplayView({ run, step }) {
    const { board } = useMemo(() => computeTttFrame(run, step), [run, step]);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1.2,
                width: Math.min(360, 320),
                p: 2,
                borderRadius: 3,
                border: '2px solid rgba(255,255,255,0.12)',
                background: 'linear-gradient(135deg, rgba(44,187,93,0.12), rgba(0,0,0,0.35))',
            }}>
                {board.flat().map((cell, idx) => (
                    <Box key={idx} sx={{
                        height: 96,
                        bgcolor: 'rgba(0,0,0,0.4)',
                        border: '2px solid rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: cell === 'X' ? '#4ECDC4' : cell === 'O' ? '#FF6B6B' : 'rgba(255,255,255,0.35)',
                        fontWeight: 'bold',
                        fontSize: '2.4rem',
                        fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                    }}>
                        {cell}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Connect Four
// ---------------------------------------------------------------------------

export function computeC4Frame(run, step) {
    const ROWS = 6;
    const COLS = 7;
    const board = Array.from({ length: ROWS }, () => Array(COLS).fill(''));
    const moves = run.move_history || [];
    const clamped = Math.max(0, Math.min(step, moves.length));
    for (let i = 0; i < clamped; i++) {
        const m = moves[i] || {};
        const col = Number.isInteger(m.column) ? m.column : (Number.isInteger(m.col) ? m.col : -1);
        const player = m.player || (i % 2 === 0 ? 'X' : 'O');
        if (col < 0 || col >= COLS) continue;
        for (let r = ROWS - 1; r >= 0; r--) {
            if (!board[r][col]) {
                board[r][col] = player;
                break;
            }
        }
    }
    const entry = clamped > 0 ? moves[clamped - 1] : null;
    const derivedScore = c4ScoreFromBoard(board);
    const score = stepScoreFromEntry(entry, derivedScore, run);
    return {
        board,
        cols: COLS,
        moves: clamped,
        score,
        finalScore: finalScore(run, score),
        outcome: outcomeLabel(run.outcome, run.result, run.winner, detectC4Winner(board)),
    };
}

export function C4ReplayView({ run, step }) {
    const { board, cols } = useMemo(() => computeC4Frame(run, step), [run, step]);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 0.75,
                width: Math.min(380, cols * 48),
                bgcolor: '#0b3d66',
                p: 1,
                borderRadius: 2,
            }}>
                {board.flat().map((cell, idx) => (
                    <Box key={idx} sx={{
                        aspectRatio: '1 / 1',
                        borderRadius: '50%',
                        bgcolor: cell === 'X' || cell === 'R' || cell === 1
                            ? '#e53935'
                            : cell === 'O' || cell === 'Y' || cell === 2
                                ? '#fdd835'
                                : 'rgba(0,0,0,0.35)',
                        border: '1px solid rgba(255,255,255,0.15)',
                    }} />
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Battleship (match live STATUS_COLORS)
// ---------------------------------------------------------------------------

export function computeBattleshipFrame(run, step) {
    const size = run.board_size
        || (run.visible_board && run.visible_board.length)
        || (run.init_state && run.init_state.visible_board && run.init_state.visible_board.length)
        || 10;
    const board = Array.from({ length: size }, () => Array(size).fill('~'));
    const moves = run.history || run.move_history || [];
    const clamped = Math.max(0, Math.min(step, moves.length));
    for (let i = 0; i < clamped; i++) {
        const m = moves[i] || {};
        const r = m.row;
        const c = m.col;
        if (!Number.isInteger(r) || !Number.isInteger(c)) continue;
        if (r < 0 || r >= size || c < 0 || c >= size) continue;
        const result = (m.result || '').toString().toLowerCase();
        if (result.includes('sunk') || m.sunk) board[r][c] = 'S';
        else if (result.includes('hit')) board[r][c] = 'H';
        else board[r][c] = 'M';
    }
    const flat = board.flat();
    const hits = flat.filter((cell) => cell === 'H' || cell === 'S').length;
    const entry = clamped > 0 ? moves[clamped - 1] : null;
    const score = stepScoreFromEntry(entry, hits, run);
    return {
        board,
        size,
        moves: clamped,
        hits,
        sunk: flat.filter((cell) => cell === 'S').length,
        score,
        finalScore: finalScore(run, score),
    };
}

const BATTLESHIP_COLORS = {
    '~': 'rgba(255,255,255,0.08)',
    M: 'rgba(0,102,204,0.3)',
    H: 'rgba(220,20,60,0.7)',
    S: 'rgba(255,215,0,0.8)',
};

export function BattleshipReplayView({ run, step }) {
    const { board, size } = useMemo(() => computeBattleshipFrame(run, step), [run, step]);
    const glyph = (cell) => {
        if (cell === 'H') return 'H';
        if (cell === 'S') return 'X';
        if (cell === 'M') return '·';
        return '';
    };
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${size}, 1fr)`,
                gap: 0.6,
                width: Math.min(360, size * 34),
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(13,71,161,0.35), rgba(0,0,0,0.4))',
                border: '1px solid rgba(255,255,255,0.12)',
            }}>
                {board.flat().map((cell, idx) => (
                    <Box key={idx} sx={{
                        aspectRatio: '1 / 1',
                        bgcolor: BATTLESHIP_COLORS[cell] || BATTLESHIP_COLORS['~'],
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                    }}>
                        {glyph(cell)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Lights Out
// ---------------------------------------------------------------------------

function toggleLights(board, row, col) {
    const n = board.length;
    const coords = [[row, col], [row - 1, col], [row + 1, col], [row, col - 1], [row, col + 1]];
    coords.forEach(([r, c]) => {
        if (r >= 0 && r < n && c >= 0 && c < n) board[r][c] = board[r][c] ? 0 : 1;
    });
}

function flattenToGrid(flat, size = 5) {
    if (!Array.isArray(flat) || flat.length !== size * size) return null;
    const grid = [];
    for (let r = 0; r < size; r++) grid.push(flat.slice(r * size, (r + 1) * size).map((v) => (v ? 1 : 0)));
    return grid;
}

export function computeLightsOutFrame(run, step) {
    const history = run.history || run.move_history || [];
    const clamped = Math.max(0, Math.min(step, history.length));

    // History entries store the board AFTER each toggle.
    // step 0 = initial (reverse first entry); step k = history[k-1].board
    if (history.length && history.some((h) => h && Array.isArray(h.board))) {
        if (clamped === 0) {
            const first = history.find((h) => h && Array.isArray(h.board));
            const board = first.board.map((row) => row.slice());
            // Reverse-apply every move up to and including the first board entry
            const firstIdx = history.indexOf(first);
            for (let i = firstIdx; i >= 0; i--) {
                const m = history[i];
                if (Number.isInteger(m?.row) && Number.isInteger(m?.col)) toggleLights(board, m.row, m.col);
            }
            return {
                board,
                size: board.length,
                moves: 0,
                lightsOn: board.flat().filter(Boolean).length,
                score: stepScoreFromEntry(null, Math.max(0, 200 - 0), run),
                finalScore: finalScore(run),
            };
        }
        const entry = history[clamped - 1];
        if (entry?.board) {
            const score = stepScoreFromEntry(entry, Math.max(0, 200 - clamped), run);
            return {
                board: entry.board,
                size: entry.board.length,
                moves: clamped,
                lightsOn: entry.board.flat().filter(Boolean).length,
                score,
                finalScore: finalScore(run, score),
            };
        }
    }

    const snaps = run.board_snapshots || [];
    if (snaps.length && history.length && snaps.length === history.length) {
        // snaps[i] == board after move i+1; synthesize init at step 0 via reverse toggle
        if (clamped === 0) {
            const board = snaps[0].map((row) => row.slice());
            const m = history[0] || {};
            if (Number.isInteger(m.row) && Number.isInteger(m.col)) toggleLights(board, m.row, m.col);
            return {
                board,
                size: board.length,
                moves: 0,
                lightsOn: board.flat().filter(Boolean).length,
                score: stepScoreFromEntry(null, Math.max(0, 200 - 0), run),
                finalScore: finalScore(run),
            };
        }
        return {
            board: snaps[clamped - 1],
            size: snaps[clamped - 1].length,
            moves: clamped,
            lightsOn: snaps[clamped - 1].flat().filter(Boolean).length,
            score: stepScoreFromEntry(history[clamped - 1], Math.max(0, 200 - clamped), run),
            finalScore: finalScore(run),
        };
    }
    if (snaps.length) {
        const idx = clamp(clamped, 0, snaps.length - 1);
        return {
            board: snaps[idx],
            size: snaps[idx].length,
            moves: clamped,
            lightsOn: snaps[idx].flat().filter(Boolean).length,
            score: stepScoreFromEntry(history[idx], Math.max(0, 200 - clamped), run),
            finalScore: finalScore(run),
        };
    }

    let board = null;
    const init = run.init_state;
    if (Array.isArray(init?.board)) board = init.board.map((row) => row.slice());
    else if (Array.isArray(run.board) && Array.isArray(run.board[0]) && !history.length) {
        board = run.board.map((row) => row.slice());
    } else if (Array.isArray(run.board_flat) && !history.length) {
        board = flattenToGrid(run.board_flat);
    } else if (Array.isArray(run.last_board) && !history.length) {
        board = flattenToGrid(run.last_board);
    }

    if (!board) {
        board = Array.from({ length: 5 }, () => Array(5).fill(0));
        // If final board is all-off and we have moves, reverse-play from solved
        if (Array.isArray(run.board) && Array.isArray(run.board[0])) {
            board = run.board.map((row) => row.slice());
            for (let i = history.length - 1; i >= 0; i--) {
                const m = history[i];
                if (Number.isInteger(m?.row) && Number.isInteger(m?.col)) toggleLights(board, m.row, m.col);
            }
        }
    }

    for (let i = 0; i < clamped; i++) {
        const m = history[i] || {};
        if (Number.isInteger(m.row) && Number.isInteger(m.col)) toggleLights(board, m.row, m.col);
    }

    const entry = clamped > 0 ? history[clamped - 1] : null;
    const score = stepScoreFromEntry(entry, Math.max(0, 200 - clamped), run);
    return {
        board,
        size: board.length,
        moves: clamped,
        lightsOn: board.flat().filter(Boolean).length,
        score,
        finalScore: finalScore(run, score),
    };
}

export function LightsOutReplayView({ run, step }) {
    const { board, size } = useMemo(() => computeLightsOutFrame(run, step), [run, step]);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${size}, 1fr)`,
                gap: 1.2,
                p: 2.5,
                borderRadius: 3,
                border: '2px solid rgba(255,255,255,0.15)',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(0,0,0,0.2))',
            }}>
                {board.flat().map((value, idx) => (
                    <Box key={idx} sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        background: value
                            ? 'linear-gradient(180deg, #FFE066, #FFC300)'
                            : 'rgba(255,255,255,0.1)',
                        boxShadow: value ? '0 0 18px rgba(255,224,102,0.55)' : 'inset 0 0 8px rgba(0,0,0,0.4)',
                        border: '1px solid rgba(255,255,255,0.2)',
                    }} />
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Minesweeper
// ---------------------------------------------------------------------------

const MS_COLORS = {
    '?': 'rgba(255,255,255,0.05)',
    F: 'rgba(255,204,0,0.7)',
    M: 'rgba(244,67,54,0.7)',
    0: 'rgba(33,150,243,0.15)',
    1: '#4FC3F7',
    2: '#66BB6A',
    3: '#FF7043',
    4: '#AB47BC',
    5: '#EF5350',
    6: '#26C6DA',
    7: '#FFCA28',
    8: '#A1887F',
};

export function computeMinesweeperFrame(run, step) {
    const history = run.history || run.move_history || [];
    const rows = run.rows || (run.visible_board && run.visible_board.length) || 9;
    const cols = run.cols || (run.visible_board && run.visible_board[0]?.length) || 9;

    // Prefer snapshots embedded in history
    if (history[Math.max(0, step - 1)]?.visible_board) {
        const idx = Math.max(0, step - 1);
        const visibleBoard = history[idx].visible_board;
        const entry = history[idx];
        const revealed = visibleBoard.flat().filter((cell) => cell !== '?' && cell !== 'F').length;
        const score = stepScoreFromEntry(entry, entry.revealed_safe ?? revealed, run);
        return {
            board: visibleBoard,
            rows,
            cols,
            moves: Math.max(0, step),
            revealed,
            flags: visibleBoard.flat().filter((cell) => cell === 'F').length,
            score,
            finalScore: finalScore(run, score),
        };
    }
    const snaps = run.board_snapshots || [];
    if (snaps.length) {
        const idx = clamp(step, 0, snaps.length - 1);
        const boardSnap = snaps[idx];
        const revealed = boardSnap.flat().filter((cell) => cell !== '?' && cell !== 'F').length;
        const entry = history[idx];
        const score = stepScoreFromEntry(entry, entry?.revealed_safe ?? revealed, run);
        return {
            board: boardSnap,
            rows: boardSnap.length,
            cols: boardSnap[0]?.length || cols,
            moves: clamp(step, 0, history.length),
            revealed,
            flags: boardSnap.flat().filter((cell) => cell === 'F').length,
            score,
            finalScore: finalScore(run, score),
        };
    }

    const board = Array.from({ length: rows }, () => Array(cols).fill('?'));
    const clamped = Math.max(0, Math.min(step, history.length));
    for (let i = 0; i < clamped; i++) {
        const m = history[i] || {};
        const r = m.row;
        const c = m.col;
        if (!Number.isInteger(r) || !Number.isInteger(c)) continue;
        if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
        if (m.action === 'flag') {
            if (m.state === 'cleared' || m.state === 'removed') board[r][c] = '?';
            else board[r][c] = board[r][c] === 'F' ? '?' : 'F';
        } else {
            const result = (m.result ?? '').toString();
            if (result === 'mine' || result === 'M') board[r][c] = 'M';
            else if (result === 'clear' || result === '') board[r][c] = '0';
            else board[r][c] = result;
        }
    }
    const entry = clamped > 0 ? history[clamped - 1] : null;
    const revealed = board.flat().filter((cell) => cell !== '?' && cell !== 'F').length;
    const score = stepScoreFromEntry(entry, entry?.revealed_safe ?? revealed, run);
    return {
        board,
        rows,
        cols,
        moves: clamped,
        revealed,
        flags: board.flat().filter((cell) => cell === 'F').length,
        score,
        finalScore: finalScore(run, score),
    };
}

export function MinesweeperReplayView({ run, step }) {
    const { board, cols } = useMemo(() => computeMinesweeperFrame(run, step), [run, step]);
    const label = (value) => {
        if (value === '0' || value === '?') return '';
        if (value === 'F') return 'F';
        if (value === 'M') return 'B';
        return value;
    };
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 0.8,
                p: 2,
                borderRadius: 3,
                border: '2px solid rgba(255,255,255,0.12)',
                background: 'linear-gradient(135deg, rgba(79,195,247,0.2), rgba(0,0,0,0.3))',
            }}>
                {board.flat().map((value, idx) => (
                    <Box key={idx} sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1,
                        background: MS_COLORS[value] || MS_COLORS['?'],
                        border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: value === '?' ? 'rgba(255,255,255,0.5)' : '#0D1117',
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                    }}>
                        {label(value)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Numberlink
// ---------------------------------------------------------------------------

const PAIR_COLORS = ['#FF7043', '#42A5F5', '#66BB6A', '#AB47BC', '#26C6DA', '#FFCA28'];

const DEFAULT_NUMBERLINK_ENDPOINTS = {
    1: [[0, 0], [4, 4]],
    2: [[0, 4], [4, 0]],
    3: [[1, 1], [3, 3]],
    4: [[1, 3], [3, 1]],
};

function defaultBaseBoard(size = 5) {
    const board = Array.from({ length: size }, () => Array(size).fill(''));
    Object.entries(DEFAULT_NUMBERLINK_ENDPOINTS).forEach(([pair, pts]) => {
        pts.forEach(([r, c]) => { board[r][c] = String(pair); });
    });
    return board;
}

export function computeNumberlinkFrame(run, step) {
    const size = run.base_board?.length
        || run.init_state?.base_board?.length
        || 5;
    const base = (run.base_board || run.init_state?.base_board || defaultBaseBoard(size))
        .map((row) => row.slice());
    const path = Array.from({ length: size }, () => Array(size).fill(''));
    const history = run.history || run.move_history || [];
    const clamped = Math.max(0, Math.min(step, history.length));

    for (let i = 0; i < clamped; i++) {
        const m = history[i] || {};
        const r = m.row;
        const c = m.col;
        const pair = m.pair != null ? String(m.pair) : '';
        if (!Number.isInteger(r) || !Number.isInteger(c)) continue;
        if (r < 0 || r >= size || c < 0 || c >= size) continue;
        if (m.action === 'undo') path[r][c] = '';
        else if (pair) path[r][c] = pair;
    }

    // If final path_board exists and no history applied, show it at last step
    if (!history.length && Array.isArray(run.path_board)) {
        const pairsDone = numberlinkPairsCompleted(run.path_board);
        return {
            base,
            path: run.path_board,
            size,
            moves: 0,
            filled: run.path_board.flat().filter(Boolean).length,
            score: stepScoreFromEntry(null, pairsDone, run),
            finalScore: finalScore(run, pairsDone),
        };
    }

    const entry = clamped > 0 ? history[clamped - 1] : null;
    const pairsDone = numberlinkPairsCompleted(path);
    const score = stepScoreFromEntry(entry, pairsDone, run);
    return {
        base,
        path,
        size,
        moves: clamped,
        filled: path.flat().filter(Boolean).length,
        score,
        finalScore: finalScore(run, score),
    };
}

export function NumberlinkReplayView({ run, step }) {
    const { base, path, size } = useMemo(() => computeNumberlinkFrame(run, step), [run, step]);
    const colorFor = (pair) => {
        const idx = (parseInt(pair, 10) || 1) - 1;
        return PAIR_COLORS[((idx % PAIR_COLORS.length) + PAIR_COLORS.length) % PAIR_COLORS.length];
    };
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${size}, 1fr)`,
                gap: 1,
                p: 2.5,
                borderRadius: 3,
                border: '2px solid rgba(255,255,255,0.15)',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.12), rgba(0,0,0,0.3))',
            }}>
                {base.map((row, rIdx) =>
                    row.map((baseValue, cIdx) => {
                        const pathValue = path[rIdx]?.[cIdx] || '';
                        const isEndpoint = Boolean(baseValue);
                        const active = pathValue || baseValue;
                        const cellColor = pathValue
                            ? colorFor(pathValue)
                            : isEndpoint
                                ? 'rgba(255,255,255,0.1)'
                                : 'rgba(0,0,0,0.2)';
                        return (
                            <Box key={`${rIdx}-${cIdx}`} sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                background: cellColor,
                                border: isEndpoint ? '2px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: pathValue ? '#0D1117' : 'rgba(255,255,255,0.85)',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                boxShadow: pathValue ? `0 0 12px ${colorFor(pathValue)}55` : 'none',
                            }}>
                                {active || ''}
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Pac-Man
// ---------------------------------------------------------------------------

const PAC_LAYOUT = [
    '#############',
    '#...........#',
    '#.###.###.###',
    '#o#.....#...#',
    '#.###.#.#.#.#',
    '#.....#...#o#',
    '###.#.###.#.#',
    '#...#.....#.#',
    '#.#####.###.#',
    '#...........#',
    '#############',
];

function pacInitialPellets() {
    const pellets = [];
    const power = [];
    PAC_LAYOUT.forEach((line, row) => {
        [...line].forEach((cell, col) => {
            if (cell === '.') pellets.push([row, col]);
            else if (cell.toLowerCase() === 'o') power.push([row, col]);
        });
    });
    return { pellets, power };
}

export function computePacManFrame(run, step) {
    const history = run.history || run.move_history || [];
    const grid = run.grid || run.init_state?.grid || PAC_LAYOUT;
    const { pellets: initPellets, power: initPower } = pacInitialPellets();
    let pellets = (run.init_state?.pellets || initPellets).map((p) => (Array.isArray(p) ? [...p] : [p[0], p[1]]));
    let power = (run.init_state?.power_pellets || initPower).map((p) => (Array.isArray(p) ? [...p] : [p[0], p[1]]));

    let pac = { position: [5, 6], direction: 'left' };
    let score = 0;
    let lives = 3;
    let powerMode = 0;
    let ghosts = run.init_state?.ghosts || [
        { position: [3, 6], mode: 'chase' },
        { position: [5, 5], mode: 'chase' },
    ];

    const clamped = Math.max(0, Math.min(step, history.length));
    for (let i = 0; i < clamped; i++) {
        const e = history[i] || {};
        if (e.position) {
            pac = {
                position: Array.isArray(e.position) ? e.position : [e.position.row, e.position.col],
                direction: e.direction || pac.direction,
            };
        } else if (e.direction && DIR_MAP[e.direction]) {
            const d = DIR_MAP[e.direction];
            pac = {
                position: [pac.position[0] + d.y, pac.position[1] + d.x],
                direction: e.direction,
            };
        }
        if (e.score != null) score = e.score;
        if (e.lives != null) lives = e.lives;
        if (e.power_mode != null) powerMode = e.power_mode;
        if (Array.isArray(e.ghosts) && e.ghosts.length) ghosts = e.ghosts;

        const [pr, pc] = pac.position;
        pellets = pellets.filter(([r, c]) => !(r === pr && c === pc));
        power = power.filter(([r, c]) => !(r === pr && c === pc));
    }

    return {
        grid,
        pac,
        ghosts,
        pellets,
        power,
        score,
        lives,
        powerMode,
        ticks: clamped,
        finalScore: finalScore(run, score),
    };
}

export function PacManReplayView({ run, step }) {
    const frame = useMemo(() => computePacManFrame(run, step), [run, step]);
    const TILE = 22;
    const grid = frame.grid;
    const cols = (grid[0] || '').length || 13;

    const content = (rowIdx, colIdx) => {
        const [pr, pc] = frame.pac.position || [];
        if (pr === rowIdx && pc === colIdx) {
            return <Box sx={{ width: TILE * 0.65, height: TILE * 0.65, borderRadius: '50%', background: '#FFEB3B' }} />;
        }
        const ghost = (frame.ghosts || []).find((g) => {
            const pos = g.position || [];
            return pos[0] === rowIdx && pos[1] === colIdx;
        });
        if (ghost) {
            const color = ghost.mode === 'fright' || frame.powerMode > 0 ? '#64B5F6' : '#EC407A';
            return <Box sx={{
                width: TILE * 0.65,
                height: TILE * 0.65,
                borderRadius: `${TILE * 0.35}px ${TILE * 0.35}px ${TILE * 0.12}px ${TILE * 0.12}px`,
                background: color,
                boxShadow: `0 0 10px ${color}88`,
            }} />;
        }
        if ((frame.pellets || []).some(([r, c]) => r === rowIdx && c === colIdx)) {
            return <Box sx={{ width: 5, height: 5, bgcolor: '#FFFDE7', borderRadius: '50%' }} />;
        }
        if ((frame.power || []).some(([r, c]) => r === rowIdx && c === colIdx)) {
            return <Box sx={{ width: 10, height: 10, bgcolor: '#FF7043', borderRadius: '50%', boxShadow: '0 0 8px #FF704388' }} />;
        }
        return null;
    };

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Score ${frame.score}`} sx={{ bgcolor: '#FFEB3B', color: '#111', fontWeight: 'bold' }} />
                <Chip size="small" label={`Lives ${frame.lives}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
                {frame.powerMode > 0 && <Chip size="small" label={`Power ${frame.powerMode}`} color="primary" />}
            </Stack>
            <Box sx={{
                display: 'inline-grid',
                gridTemplateColumns: `repeat(${cols}, ${TILE}px)`,
                border: '4px solid rgba(255,255,255,0.3)',
                borderRadius: 3,
                background: '#000',
                boxShadow: '0 0 22px rgba(255,235,59,0.3)',
                overflow: 'hidden',
            }}>
                {grid.map((row, rowIdx) => {
                    const cells = typeof row === 'string' ? row.split('') : row;
                    return cells.map((cell, colIdx) => (
                        <Box key={`${rowIdx}-${colIdx}`} sx={{
                            width: TILE,
                            height: TILE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: cell === '#' ? 'linear-gradient(135deg, #1A237E, #0D47A1)' : 'transparent',
                            border: cell === '#' ? '1px solid rgba(255,255,255,0.06)' : 'none',
                        }}>
                            {cell !== '#' ? content(rowIdx, colIdx) : null}
                        </Box>
                    ));
                })}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Road Fighter
// ---------------------------------------------------------------------------

export function computeRoadFighterFrame(run, step) {
    const history = run.history || run.move_history || [];
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    let lane = run.init_state?.player?.lane ?? run.player?.lane ?? 1;

    // Replay lane changes when entries omit an explicit lane field
    for (let i = 0; i <= clamped && i < history.length; i++) {
        const e = history[i] || {};
        const action = (e.action || '').toString().toLowerCase();
        if (Number.isInteger(e.lane)) lane = e.lane;
        else if (e.player?.lane != null) lane = e.player.lane;
        else if (action.includes('lane_left') || action === 'left') lane = Math.max(0, lane - 1);
        else if (action.includes('lane_right') || action === 'right') lane = Math.min(2, lane + 1);
    }

    const entry = history[clamped] || {};
    if (Number.isInteger(entry.lane)) lane = entry.lane;
    if (entry.player?.lane != null) lane = entry.player.lane;

    const distance = entry.distance ?? run.player?.distance ?? 0;
    const speed = entry.speed ?? run.player?.speed ?? 0;
    const fuel = entry.fuel ?? run.player?.fuel ?? 0;
    const damage = entry.damage ?? run.player?.damage ?? 0;
    const skidding = Boolean(entry.skidding);
    const trackLength = run.track_length || 2000;
    const obstacles = (entry.obstacles || run.obstacles || []).filter(Boolean);
    const fuelPacks = (entry.fuel_packs || entry.fuelPacks || run.fuel_packs || run.fuelPacks || []).filter(Boolean);
    const healthPacks = (entry.health_packs || entry.healthPacks || run.health_packs || run.healthPacks || []).filter(Boolean);
    const derivedScore = Math.round(Number(entry.campaign_distance ?? distance) || 0);
    const score = stepScoreFromEntry(entry, derivedScore, run, Math.round(distance));

    return {
        lane,
        distance,
        speed,
        fuel,
        damage,
        trackLength,
        obstacles,
        fuelPacks,
        healthPacks,
        tick: entry.tick ?? clamped,
        level: entry.level ?? run.level ?? 1,
        skidding,
        score,
        finalScore: finalScore(run, score),
    };
}

export function RoadFighterReplayView({ run, step }) {
    const frame = useMemo(() => computeRoadFighterFrame(run, step), [run, step]);
    const progress = clamp((frame.distance / frame.trackLength) * 100, 0, 100);

    const remainingM = Math.max(0, frame.trackLength - frame.distance);

    return (
        <Box>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Lv ${frame.level || 1}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
                <Chip size="small" label={`${Math.round(frame.speed)} km/h`} sx={{ bgcolor: THEME.primary, color: '#111', fontWeight: 'bold' }} />
                <Chip size="small" label={`Dmg ${Math.round(Number(frame.damage) || 0)}%`} sx={{ bgcolor: frame.damage >= 75 ? THEME.accent : 'rgba(255,255,255,0.08)', color: 'white' }} />
                <Chip size="small" label={`${Math.round(frame.distance)} m`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
            </Stack>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: 1.5,
            }}>
                <TrackProgressMeter value={progress} remainingM={remainingM} color="#4ECDC4" />
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 1.5,
                    borderRadius: 3,
                    border: '1px solid rgba(78,205,196,0.25)',
                    background: 'linear-gradient(180deg, #0c1a18, #0a1210)',
                }}>
                    <RoadFighterScene
                        lane={frame.lane}
                        distance={frame.distance}
                        obstacles={frame.obstacles}
                        fuelPacks={frame.fuelPacks}
                        healthPacks={frame.healthPacks}
                        level={frame.level}
                        skidding={frame.skidding}
                        trackLength={frame.trackLength}
                        width={260}
                        height={400}
                    />
                </Box>
                <FuelMeter value={frame.fuel} />
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Two-Joint Pole
// ---------------------------------------------------------------------------

function tjpPlayerPhysics(source, playerKey) {
    if (!source || typeof source !== 'object') return null;
    const nested = source.players?.[playerKey];
    if (nested) return nested.physics || nested;
    const flat = source[playerKey];
    if (flat) return flat.physics || flat;
    return null;
}

/** True when a run / history tick carries dual-cart multiplayer state. */
export function isTwoJointPoleMultiplayerRun(run, entry) {
    const gameId = String(run?.game_id || run?.game || '').toLowerCase();
    if (gameId.includes('multiplayer')) return true;
    if (run?.init_state?.players?.player_1 && run?.init_state?.players?.player_2) return true;
    if (entry?.players?.player_1 && entry?.players?.player_2) return true;
    if (entry?.player_1 && entry?.player_2) return true;
    return false;
}

/** Human label for a round result encoded on a history tick / training_stats. */
export function formatTjpRoundResult(result, winnerId) {
    const raw = result ?? winnerId ?? null;
    if (raw == null || raw === '') return null;
    const s = String(raw).toLowerCase();
    if (s === 'draw' || s === 'none' || s === 'null') return 'Draw';
    if (s === 'player_1' || s === 'p1') return 'P1 wins';
    if (s === 'player_2' || s === 'p2') return 'P2 wins';
    return String(raw);
}

/**
 * Derive round start/finish markers from a history tick.
 * Prefers explicit GM `round_event`; falls back for older mega-session runs.
 */
export function resolveTjpRoundEvent(entry, prevEntry) {
    if (!entry || typeof entry !== 'object') {
        return { roundEvent: null, roundResult: null };
    }
    let roundEvent = entry.round_event || entry.event || null;
    if (roundEvent && !['start', 'finish', 'round_start', 'round_end', 'round_over'].includes(String(roundEvent))) {
        // Ignore unrelated event strings (e.g. pole_touched_ground on seat history).
        if (!String(roundEvent).startsWith('round')) roundEvent = null;
    }
    if (roundEvent === 'round_start') roundEvent = 'start';
    if (roundEvent === 'round_end' || roundEvent === 'round_over') roundEvent = 'finish';

    if (!roundEvent) {
        if (entry.round_over || entry.soft_game_over || entry.hard_game_over || entry.session_over) {
            roundEvent = 'finish';
        } else if (
            entry.round_step === 0
            || (prevEntry
                && entry.round_index != null
                && prevEntry.round_index != null
                && Number(entry.round_index) !== Number(prevEntry.round_index))
        ) {
            roundEvent = 'start';
        }
    }

    let roundResult = entry.round_result ?? null;
    if (!roundResult && roundEvent === 'finish') {
        if (entry.winner_id) roundResult = entry.winner_id;
        else if (entry.round_over || entry.soft_game_over || entry.hard_game_over) roundResult = 'draw';
    }
    return { roundEvent, roundResult };
}

export function computeTwoJointPoleFrame(run, step) {
    const history = run.history || run.move_history || [];
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    const entry = history[clamped] || {};
    const prevEntry = clamped > 0 ? (history[clamped - 1] || {}) : null;

    if (isTwoJointPoleMultiplayerRun(run, entry) || (history.length === 0 && run?.init_state?.players)) {
        const initP1 = tjpPlayerPhysics(run.init_state, 'player_1') || {};
        const initP2 = tjpPlayerPhysics(run.init_state, 'player_2') || {};
        const p1 = tjpPlayerPhysics(entry, 'player_1') || initP1;
        const p2 = tjpPlayerPhysics(entry, 'player_2') || initP2;
        const scores = entry.scores || {
            player_1: entry.players?.player_1?.score ?? run.player_1_score,
            player_2: entry.players?.player_2?.score ?? run.player_2_score,
        };
        const { roundEvent, roundResult } = resolveTjpRoundEvent(entry, prevEntry);
        const trainingStats = run.training_stats
            || run.meta?.training_stats
            || null;
        const numEpisodes = entry.num_episodes
            ?? trainingStats?.num_episodes
            ?? run.num_episodes
            ?? run.meta?.num_episodes
            ?? null;
        const roundIndex = entry.round_index
            ?? trainingStats?.round_index
            ?? 1;
        return {
            multiplayer: true,
            player_1: p1,
            player_2: p2,
            actions: entry.actions || null,
            scores,
            winner_id: entry.winner_id ?? run.winner ?? null,
            step: clamped,
            trackLimit: TJP_TRACK_LIMIT,
            score: Math.max(Number(scores?.player_1) || 0, Number(scores?.player_2) || 0),
            finalScore: finalScore(run),
            // Round / mega-session spectator cues
            round_index: roundIndex,
            num_episodes: numEpisodes,
            rounds_completed: entry.rounds_completed ?? trainingStats?.rounds_completed ?? null,
            round_event: roundEvent,
            round_result: roundResult,
            round_result_label: formatTjpRoundResult(roundResult, entry.winner_id),
            round_over: Boolean(entry.round_over || roundEvent === 'finish'),
            soft_game_over: Boolean(entry.soft_game_over),
            hard_game_over: Boolean(entry.hard_game_over || entry.session_over || entry.is_game_over),
            terminated_reason: entry.terminated_reason || null,
            message: entry.message || null,
            mode: entry.mode || run.mode || run.meta?.mode || trainingStats?.mode || null,
            round_results: trainingStats?.round_results || run.round_results || null,
            // SP-shaped fields from P1 so shared stats helpers stay safe
            cart_position: p1.cart_position ?? 0,
            pole1_angle: p1.pole1_angle ?? 0,
            pole2_angle: p1.pole2_angle ?? 0,
            action: entry.actions?.player_1,
            reward: null,
        };
    }

    const state = entry.state || entry.physics || run.physics || run.init_state?.physics || {};
    return {
        multiplayer: false,
        cart_position: state.cart_position ?? 0,
        cart_velocity: state.cart_velocity ?? 0,
        pole1_angle: state.pole1_angle ?? 0,
        pole2_angle: state.pole2_angle ?? 0,
        pole1_velocity: state.pole1_velocity ?? 0,
        pole2_velocity: state.pole2_velocity ?? 0,
        action: entry.action,
        reward: entry.reward,
        step: clamped,
        trackLimit: TJP_TRACK_LIMIT,
        score: cumulativeScore(history, clamped + 1, (item) => item?.reward),
        finalScore: finalScore(run),
    };
}

export function TwoJointPoleReplayView({ run, step }) {
    const frame = useMemo(() => computeTwoJointPoleFrame(run, step), [run, step]);

    if (frame.multiplayer) {
        const p1 = frame.player_1 || {};
        const p2 = frame.player_2 || {};
        const a1 = frame.actions?.player_1;
        const a2 = frame.actions?.player_2;
        const showRoundHud = frame.mode === 'training'
            || frame.round_index != null
            || frame.round_event
            || (Array.isArray(frame.round_results) && frame.round_results.length > 0);
        const roundLabel = frame.num_episodes != null
            ? `Round ${frame.round_index ?? '—'}/${frame.num_episodes}`
            : (frame.round_index != null ? `Round ${frame.round_index}` : null);
        const eventBanner = frame.round_event === 'start'
            ? { label: `${roundLabel || 'Round'} start`, color: '#4ECDC4' }
            : frame.round_event === 'finish'
                ? {
                    label: `${roundLabel || 'Round'} finish — ${frame.round_result_label || 'Over'}`,
                    color: frame.round_result === 'draw' ? '#ffc107'
                        : frame.round_result === 'player_1' ? '#4ECDC4'
                            : frame.round_result === 'player_2' ? '#E8A87C'
                                : THEME.primary,
                }
                : null;

        return (
            <Box>
                {showRoundHud && (
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                        {roundLabel && (
                            <Chip
                                size="small"
                                label={roundLabel}
                                sx={{ bgcolor: 'rgba(255, 193, 7, 0.12)', color: '#ffc107', fontWeight: 700 }}
                            />
                        )}
                        {frame.mode === 'training' && (
                            <Chip
                                size="small"
                                label="Training"
                                sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: THEME.secondary, fontWeight: 600 }}
                            />
                        )}
                        {frame.soft_game_over && (
                            <Chip size="small" label="Soft round over" sx={{ color: THEME.secondary }} />
                        )}
                        {frame.hard_game_over && (
                            <Chip size="small" label="Session over" sx={{ color: THEME.accent, fontWeight: 700 }} />
                        )}
                    </Stack>
                )}
                {eventBanner && (
                    <Box sx={{
                        mb: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: `1px solid ${eventBanner.color}55`,
                        bgcolor: `${eventBanner.color}18`,
                        textAlign: 'center',
                    }}>
                        <Typography variant="subtitle2" sx={{ color: eventBanner.color, fontWeight: 800 }}>
                            {eventBanner.label}
                        </Typography>
                        {frame.round_event === 'finish' && frame.terminated_reason && (
                            <Typography variant="caption" sx={{ color: THEME.secondary, display: 'block' }}>
                                {frame.terminated_reason}
                            </Typography>
                        )}
                    </Box>
                )}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                    {a1 && <Chip size="small" label={`P1 ${a1}`} sx={{ bgcolor: '#4ECDC4', color: '#111', fontWeight: 'bold' }} />}
                    {a2 && <Chip size="small" label={`P2 ${a2}`} sx={{ bgcolor: '#E8A87C', color: '#111', fontWeight: 'bold' }} />}
                    <Chip size="small" label={`P1 ${Number(frame.scores?.player_1 ?? 0).toFixed(1)}`} variant="outlined" sx={{ color: '#4ECDC4', borderColor: 'rgba(78,205,196,0.45)' }} />
                    <Chip size="small" label={`P2 ${Number(frame.scores?.player_2 ?? 0).toFixed(1)}`} variant="outlined" sx={{ color: '#E8A87C', borderColor: 'rgba(232,168,124,0.45)' }} />
                    <Chip size="small" label={`x₁ ${Number(p1.cart_position ?? 0).toFixed(2)}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
                    <Chip size="small" label={`x₂ ${Number(p2.cart_position ?? 0).toFixed(2)}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
                </Stack>
                <Box sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(142,140,216,0.3)',
                    overflow: 'hidden',
                    background: '#0c1a24',
                }}>
                    <TwoJointPoleSceneMultiplayer
                        player1_physics={p1}
                        player2_physics={p2}
                        width={560}
                        height={320}
                    />
                </Box>
            </Box>
        );
    }

    const pole1Deg = ((frame.pole1_angle * 180) / Math.PI).toFixed(1);
    const pole2Deg = ((frame.pole2_angle * 180) / Math.PI).toFixed(1);

    return (
        <Box>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                {frame.action && <Chip size="small" label={String(frame.action)} sx={{ bgcolor: THEME.primary, color: '#111', fontWeight: 'bold' }} />}
                {frame.reward != null && (
                    <Chip size="small" label={`Reward ${Number(frame.reward).toFixed(2)}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
                )}
                <Chip size="small" label={`θ₁ ${pole1Deg}°`} variant="outlined" sx={{ color: '#e8b84a', borderColor: 'rgba(232,184,74,0.45)' }} />
                <Chip size="small" label={`θ₂ ${pole2Deg}°`} variant="outlined" sx={{ color: '#2cbb5d', borderColor: 'rgba(44,187,93,0.45)' }} />
                <Chip size="small" label={`x ${Number(frame.cart_position).toFixed(2)}`} variant="outlined" sx={{ color: '#4ECDC4', borderColor: 'rgba(78,205,196,0.45)' }} />
            </Stack>
            <Box sx={{
                borderRadius: 3,
                border: '1px solid rgba(78,205,196,0.22)',
                overflow: 'hidden',
                background: '#0c1a24',
            }}>
                <TwoJointPoleScene
                    cart_position={frame.cart_position}
                    pole1_angle={frame.pole1_angle}
                    pole2_angle={frame.pole2_angle}
                    width={480}
                    height={300}
                />
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Blackjack — casino felt
// ---------------------------------------------------------------------------

function parseCard(card) {
    const s = String(card || '');
    const suit = s.slice(-1).toUpperCase();
    const rank = s.slice(0, -1) || s;
    const red = suit === 'H' || suit === 'D';
    const suitGlyph = { H: '♥', D: '♦', C: '♣', S: '♠' }[suit] || suit;
    return { rank, suit, suitGlyph, red };
}

function handTotal(hand) {
    let total = 0;
    let aces = 0;
    (hand || []).forEach((card) => {
        const { rank } = parseCard(card);
        if (rank === 'A') { total += 11; aces += 1; }
        else if (['K', 'Q', 'J'].includes(rank)) total += 10;
        else total += Number(rank) || 0;
    });
    while (total > 21 && aces > 0) { total -= 10; aces -= 1; }
    return total;
}

function PlayingCard({ card, highlight }) {
    const { rank, suitGlyph, red } = parseCard(card);
    return (
        <Box sx={{
            width: 54,
            height: 76,
            borderRadius: 1.5,
            bgcolor: highlight ? '#e8fff0' : '#f8f6f0',
            border: highlight ? `2px solid ${THEME.primary}` : '1px solid rgba(0,0,0,0.2)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.6,
            color: red ? '#c62828' : '#1a1a1a',
            fontWeight: 800,
            fontFamily: '"Fira Code", "JetBrains Mono", monospace',
        }}>
            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1, color: 'inherit', fontWeight: 800 }}>{rank}</Typography>
            <Typography sx={{ fontSize: '1.35rem', lineHeight: 1, color: 'inherit' }}>{suitGlyph}</Typography>
            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1, color: 'inherit', fontWeight: 800, transform: 'rotate(180deg)' }}>{rank}</Typography>
        </Box>
    );
}

export function computeBlackjackFrame(run, step) {
    const history = timeline(run);
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    const entry = history[clamped] || {};
    const bankroll = entry.bankroll ?? entry.credits ?? run.bankroll ?? run.credits ?? null;
    const score = stepScoreFromEntry(entry, bankroll, run);
    return {
        entry,
        bankroll,
        rounds: history.length ? clamped + 1 : 0,
        score,
        finalScore: finalScore(run, score),
        outcome: outcomeLabel(entry.result, run.result),
    };
}

export function BlackjackReplayView({ run, step }) {
    const { entry } = useMemo(() => computeBlackjackFrame(run, step), [run, step]);
    const playerHand = entry.player_hand || run.player_hand || [];
    const dealerHand = entry.dealer_hand || run.dealer_hand || [];
    const playerTotal = handTotal(playerHand);
    const dealerTotal = handTotal(dealerHand);
    const bank = entry.bankroll ?? entry.credits ?? run.bankroll;

    return (
        <Box sx={{
            borderRadius: 3,
            p: 3,
            background: 'radial-gradient(ellipse at center, #1b5e3a 0%, #0d3b24 55%, #082216 100%)',
            border: '3px solid #c9a227',
            boxShadow: 'inset 0 0 40px rgba(0,0,0,0.35), 0 0 0 2px rgba(201,162,39,0.25)',
        }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mb: 1, letterSpacing: 2, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Dealer {dealerHand.length ? `· ${dealerTotal}` : ''}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3, minHeight: 76 }}>
                {dealerHand.length
                    ? dealerHand.map((c, i) => <PlayingCard key={i} card={c} />)
                    : <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>—</Typography>}
            </Stack>

            <Box sx={{
                height: 2,
                mb: 2.5,
                background: 'linear-gradient(90deg, transparent, rgba(201,162,39,0.5), transparent)',
            }} />

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mb: 1, letterSpacing: 2, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Player {playerHand.length ? `· ${playerTotal}` : ''}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2, minHeight: 76 }}>
                {playerHand.length
                    ? playerHand.map((c, i) => <PlayingCard key={i} card={c} highlight={playerTotal === 21} />)
                    : <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>—</Typography>}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {entry.result && (
                    <Chip label={String(entry.result).replace(/_/g, ' ')} sx={{ bgcolor: THEME.primary, color: '#111', fontWeight: 'bold', textTransform: 'capitalize' }} />
                )}
                {entry.payout != null && (
                    <Chip label={`Payout ${entry.payout}`} variant="outlined" sx={{ color: '#c9a227', borderColor: '#c9a227' }} />
                )}
                {bank != null && (
                    <Chip label={`Bank ${bank}`} sx={{ bgcolor: 'rgba(0,0,0,0.35)', color: '#e8d48b' }} />
                )}
            </Stack>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// One-Armed Bandit — slot machine chrome
// ---------------------------------------------------------------------------

const REEL_STYLE = {
    Seven: { label: '7', color: '#ef4743' },
    Bell: { label: 'BELL', color: '#FFD700' },
    Star: { label: 'STAR', color: '#4ECDC4' },
    Lemon: { label: 'LEM', color: '#c6d63c' },
    Cherry: { label: 'CHR', color: '#e53935' },
    Bar: { label: 'BAR', color: '#f5f5f5' },
};

export function computeBanditFrame(run, step) {
    const history = run.history || run.spin_history || [];
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    const entry = history[clamped] || run.last_spin || {};
    const balance = entry.credits_after ?? entry.balance ?? run.credits ?? run.balance ?? null;
    const score = stepScoreFromEntry(entry, balance, run);
    return {
        entry,
        reels: entry.reels || ['?', '?', '?'],
        spins: history.length ? clamped + 1 : 0,
        balance,
        score,
        finalScore: finalScore(run, score),
    };
}

export function BanditReplayView({ run, step }) {
    const { entry, reels, spins } = useMemo(() => computeBanditFrame(run, step), [run, step]);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                width: '100%',
                maxWidth: 380,
                borderRadius: 3,
                p: 2.5,
                background: 'linear-gradient(160deg, #2a2a2e 0%, #141416 60%, #0c0c0e 100%)',
                border: '3px solid #c9a227',
                boxShadow: '0 12px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
            }}>
                <Typography sx={{
                    textAlign: 'center',
                    color: '#c9a227',
                    fontWeight: 800,
                    letterSpacing: 3,
                    mb: 2,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                }}>
                    One-Armed Bandit · Spin {Math.max(spins, 1)}
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1.2,
                    p: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(180deg, #0a3d28, #062818)',
                    border: '2px solid rgba(201,162,39,0.45)',
                    mb: 2,
                }}>
                    {reels.map((sym, i) => {
                        const style = REEL_STYLE[sym] || { label: String(sym).slice(0, 4).toUpperCase(), color: '#FFD700' };
                        return (
                            <Box key={i} sx={{
                                height: 88,
                                borderRadius: 1.5,
                                bgcolor: '#0a0a0a',
                                border: '1px solid rgba(255,255,255,0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: 'inset 0 0 18px rgba(0,0,0,0.6)',
                            }}>
                                <Typography sx={{
                                    color: style.color,
                                    fontWeight: 900,
                                    fontSize: style.label.length > 2 ? '1.1rem' : '2rem',
                                    letterSpacing: 1,
                                    textShadow: `0 0 12px ${style.color}66`,
                                    fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                                }}>
                                    {style.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {entry.bet != null && <Chip size="small" label={`Bet ${entry.bet}`} sx={{ bgcolor: 'rgba(201,162,39,0.15)', color: '#c9a227' }} />}
                    {entry.payout != null && <Chip size="small" label={`Payout ${entry.payout}`} sx={{ bgcolor: THEME.primary, color: '#111', fontWeight: 'bold' }} />}
                    {(entry.net != null) && <Chip size="small" label={`Net ${entry.net}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />}
                    {entry.credits_after != null && <Chip size="small" label={`Credits ${entry.credits_after}`} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />}
                </Stack>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Pong
// ---------------------------------------------------------------------------

export function computePongFrame(run, step) {
    const history = run.history || run.move_history || [];
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    const entry = history[clamped] || {};
    const score = entry.score || { player: 0, opponent: 0 };
    const goals = entry.goals || {};
    const playerHits = entry.player_hits ?? entry.hits?.player ?? entry.rallies?.player ?? 0;
    const opponentHits = entry.opponent_hits ?? entry.hits?.opponent ?? entry.rallies?.opponent ?? 0;
    return {
        entry,
        ball: entry.ball || { x: 0.5, y: 0.5 },
        paddles: entry.paddles || { player: 0.5, opponent: 0.5 },
        score,
        goals,
        playerHits,
        opponentHits,
        ticks: clamped,
        finalScore: finalScore(run),
    };
}

export function PongReplayView({ run, step }) {
    const { ball, paddles, score, goals } = useMemo(() => computePongFrame(run, step), [run, step]);
    const scoreLabel = goals
        ? `${score.player ?? 0}  ·  ${score.opponent ?? 0}   (${goals.player ?? 0}–${goals.opponent ?? 0})`
        : `${score.player ?? 0}  ·  ${score.opponent ?? 0}`;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 420,
                aspectRatio: '16 / 9',
                bgcolor: '#050505',
                border: `1px solid ${THEME.border}`,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 'inset 0 0 40px rgba(44,187,93,0.08)',
            }}>
                <Box sx={{
                    position: 'absolute', left: '50%', top: 8, bottom: 8, width: 0,
                    borderLeft: '2px dashed rgba(255,255,255,0.15)',
                    transform: 'translateX(-50%)',
                }} />
                <Typography sx={{
                    position: 'absolute', top: 10, left: 0, right: 0,
                    textAlign: 'center', color: 'rgba(255,255,255,0.55)',
                    fontFamily: '"Fira Code", monospace', fontSize: '0.95rem', letterSpacing: 4,
                }}>
                    {scoreLabel}
                </Typography>
                <Box sx={{
                    position: 'absolute', left: 10,
                    top: `${(paddles.player ?? 0.5) * 100}%`,
                    width: 10, height: 64, bgcolor: THEME.primary,
                    transform: 'translateY(-50%)', borderRadius: 1,
                    boxShadow: `0 0 12px ${THEME.primary}66`,
                }} />
                <Box sx={{
                    position: 'absolute', right: 10,
                    top: `${(paddles.opponent ?? 0.5) * 100}%`,
                    width: 10, height: 64, bgcolor: THEME.accent,
                    transform: 'translateY(-50%)', borderRadius: 1,
                }} />
                <Box sx={{
                    position: 'absolute',
                    left: `${(ball.x ?? 0.5) * 100}%`,
                    top: `${(ball.y ?? 0.5) * 100}%`,
                    width: 12, height: 12, bgcolor: '#FFD700', borderRadius: '50%',
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 0 14px rgba(255,215,0,0.55)',
                }} />
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Santa Drop
// ---------------------------------------------------------------------------

export function computeSantaDropFrame(run, step) {
    const history = run.history || run.move_history || [];
    const last = Math.max(history.length - 1, 0);
    const clamped = Math.max(0, Math.min(step, last));
    const entry = (history[clamped] && typeof history[clamped] === 'object') ? history[clamped] : {};
    return {
        entry,
        houses: Array.isArray(entry.houses) ? entry.houses : [],
        gifts: Array.isArray(entry.gifts) ? entry.gifts : (Array.isArray(entry.gifts_in_air) ? entry.gifts_in_air : []),
        sledPos: typeof entry.sled_pos === 'number' ? entry.sled_pos : 90,
        dropLead: typeof entry.drop_lead === 'number' ? entry.drop_lead : 28,
        // undefined for old runs -> the scene falls back to the mirrored rule
        aimHouseId: 'aim_house_id' in entry ? entry.aim_house_id : undefined,
        maxSteps: run.meta?.max_steps || run.init_state?.max_steps || 1200,
        tick: entry.tick ?? clamped,
        score: entry.score ?? 0,
        landed: entry.gifts_landed ?? 0,
        dropped: entry.gifts_dropped ?? 0,
        combo: entry.combo ?? 0,
        bursts: burstsFromTicks(history, clamped),
        isLast: history.length > 0 && clamped === last,
        finalScore: finalScore(run),
    };
}

/** Runs recorded before the rooftop rules have no houses; show them as plain history. */
export function isLegacySantaDropRun(run = {}) {
    const history = run.history || run.move_history || [];
    return !history.some((h) => h && Array.isArray(h.houses));
}

export function SantaDropReplayView({ run, step }) {
    const frame = useMemo(() => computeSantaDropFrame(run, step), [run, step]);
    if (isLegacySantaDropRun(run)) return <HistoryReplayView run={run} step={step} />;
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 520 }}>
                <SantaDropScene
                    houses={frame.houses}
                    gifts={frame.gifts}
                    sledPos={frame.sledPos}
                    dropLead={frame.dropLead}
                    aimHouseId={frame.aimHouseId}
                    maxSteps={frame.maxSteps}
                    tick={frame.tick}
                    bursts={frame.bursts}
                    overlay={frame.isLast && step > 0 ? 'over' : null}
                    overlayText={frame.isLast ? `${frame.landed}/${frame.dropped} delivered` : undefined}
                    data-testid="santa-drop-replay-scene"
                />
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Tetris
// ---------------------------------------------------------------------------

/** GM board kinds 1–7 map to TETROMINOES key order: I, O, T, S, Z, J, L. */
const TETRIS_KIND_COLORS = Object.values(TETROMINOES).map((t) => t.color);
/** Larger cells for ReplayPlayer (live play uses 36px). */
export const TETRIS_REPLAY_CELL = 28;

export function tetrisReplayCellColor(cell) {
    if (!cell) return 0;
    if (typeof cell === 'string' && cell.startsWith('#')) return cell;
    const n = Number(cell);
    if (Number.isFinite(n) && n >= 1 && n <= 7) return TETRIS_KIND_COLORS[(n - 1) % 7];
    if (n) return TETRIS_KIND_COLORS[Math.abs(n) % 7];
    return 0;
}

export function colorizeTetrisReplayBoard(board) {
    return (board || []).map((row) => (row || []).map((cell) => tetrisReplayCellColor(cell)));
}

export function computeTetrisFrame(run, step) {
    const history = run.history || run.board_snapshots || [];
    const cols = run.cols || run.init_state?.cols || 10;
    const rows = run.rows || run.init_state?.rows || 20;
    const empty = () => Array.from({ length: rows }, () => Array(cols).fill(0));
    if (!history.length) {
        const board = run.init_state?.board || empty();
        return { board, score: run.score || 0, cols: (board[0] && board[0].length) || cols };
    }
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    const entry = history[clamped] || {};
    const board = Array.isArray(entry.board) ? entry.board : (run.init_state?.board || empty());
    return {
        board,
        score: entry.score ?? run.score ?? 0,
        cols: (board[0] && board[0].length) || cols,
        lines: entry.lines ?? run.lines ?? 0,
        ticks: clamped,
        finalScore: finalScore(run, entry.score ?? run.score ?? 0),
    };
}

export function TetrisReplayView({ run, step }) {
    const canvasRef = useRef(null);
    const { board, cols, rows } = useMemo(() => {
        const frame = computeTetrisFrame(run, step);
        const colored = colorizeTetrisReplayBoard(frame.board);
        return {
            board: colored,
            cols: frame.cols || (colored[0] && colored[0].length) || 10,
            rows: colored.length || 20,
        };
    }, [run, step]);

    const cellSize = TETRIS_REPLAY_CELL;
    const canvasW = cols * cellSize;
    const canvasH = rows * cellSize;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        drawTetrisBoard(ctx, {
            board,
            currentPiece: null,
            boardWidth: cols,
            boardHeight: rows,
            cellWidth: cellSize,
            cellHeight: cellSize,
            canvasWidth: canvasW,
            canvasHeight: canvasH,
        });
    }, [board, cols, rows, cellSize, canvasW, canvasH]);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 0.5 }}>
            <canvas
                ref={canvasRef}
                width={canvasW}
                height={canvasH}
                aria-label={`${STACKFALL_DISPLAY_NAME} replay board`}
                style={{
                    display: 'block',
                    width: 'auto',
                    height: 'auto',
                    maxWidth: 'min(100%, 320px)',
                    maxHeight: 'min(72vh, 640px)',
                    objectFit: 'contain',
                    aspectRatio: `${cols} / ${rows}`,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: 6,
                    backgroundColor: '#0a0a0a',
                }}
            />
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Space Invader (shared SpaceInvaderScene — same canvas as live play)
// ---------------------------------------------------------------------------

export {
    SI_CANVAS_W,
    SI_CANVAS_H,
    SI_PLAYER_SIZE,
    SI_INVADER_SIZE,
    SI_INVADER_ROWS,
    SI_INVADER_COLS,
    siInvaderTypeFromRow,
};

export function siDefaultInvaderLayout() {
    return createInvaderGrid();
}

function siInvaderFromCell(cell, row, col) {
    if (cell == null || cell === false || cell === 0 || cell === '') return null;
    const type = typeof cell === 'string'
        ? cell
        : (cell.type || siInvaderTypeFromRow(row));
    return {
        x: Number.isFinite(Number(cell.x)) ? Number(cell.x) : col * (SI_INVADER_SIZE + 15) + 60,
        y: Number.isFinite(Number(cell.y)) ? Number(cell.y) : row * (SI_INVADER_SIZE + 15) + 60,
        width: cell.width || SI_INVADER_SIZE,
        height: cell.height || SI_INVADER_SIZE,
        alive: true,
        type,
    };
}

export function normalizeSpaceInvaders(raw) {
    if (Array.isArray(raw)) {
        if (!raw.length) return [];
        if (Array.isArray(raw[0])) {
            const invaders = [];
            raw.forEach((row, r) => {
                (row || []).forEach((cell, c) => {
                    const inv = siInvaderFromCell(cell, r, c);
                    if (inv) invaders.push(inv);
                });
            });
            return invaders;
        }
        if (typeof raw[0] === 'object' && raw[0] != null) {
            return raw
                .filter((inv) => inv && inv.alive !== false)
                .map((inv, i) => {
                    const col = i % SI_INVADER_COLS;
                    const row = Math.floor(i / SI_INVADER_COLS);
                    return siInvaderFromCell(inv, row, col);
                })
                .filter(Boolean);
        }
        const layout = siDefaultInvaderLayout();
        if (raw.every((v) => v === 0 || v === 1 || typeof v === 'boolean')) {
            return layout.filter((_, i) => raw[i]);
        }
        return layout;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) {
        const count = Math.max(0, Math.min(SI_INVADER_ROWS * SI_INVADER_COLS, Math.trunc(n)));
        return siDefaultInvaderLayout().slice(0, count);
    }
    return siDefaultInvaderLayout();
}

export function normalizeSpaceInvaderBunkers(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map((b, i) => {
        if (b == null || b === false || b === 0) return null;
        if (typeof b !== 'object') {
            return { x: 80 + i * 220, y: 520, width: 70, height: 40, health: 1 };
        }
        if (b.alive === false || (b.health != null && Number(b.health) <= 0)) return null;
        return {
            x: Number.isFinite(Number(b.x)) ? Number(b.x) : 80 + i * 220,
            y: Number.isFinite(Number(b.y)) ? Number(b.y) : 520,
            width: b.width || 70,
            height: b.height || 40,
            health: b.health ?? 1,
        };
    }).filter(Boolean);
}

function siInvaderKey(inv) {
    return `${Math.round(Number(inv?.x) || 0)}:${Math.round(Number(inv?.y) || 0)}:${inv?.type || ''}`;
}

/** When aliens vanish between ticks, show the player shot that caused the hit. */
export function bulletsForVanishedInvaders(prevInvaders = [], nextInvaders = [], existing = []) {
    const nextKeys = new Set((nextInvaders || []).map(siInvaderKey));
    const extras = [];
    for (let i = 0; i < (prevInvaders || []).length; i++) {
        const inv = prevInvaders[i];
        if (!inv || nextKeys.has(siInvaderKey(inv))) continue;
        const hitX = (Number(inv.x) || 0) + (Number(inv.width) || SI_INVADER_SIZE) / 2;
        const hitY = Number(inv.y) || 0;
        const already = (existing || []).some((b) => (
            b
            && b.type !== 'invader'
            && Math.abs((Number(b.x) || 0) - hitX) < 48
            && Math.abs((Number(b.y) || 0) - hitY) < 56
        ));
        if (already) continue;
        extras.push({
            x: hitX - SI_BULLET_W / 2,
            y: hitY,
            dy: -8,
            dir: 'up',
            type: 'player',
            width: SI_BULLET_W,
            height: 10,
        });
    }
    return extras;
}

/** Advance previous-frame shots so sparse recordings still show in-flight bullets. */
export function interpolateSpaceInvaderBullets(prevBullets = [], framesSkipped = 1) {
    const skip = Number.isFinite(Number(framesSkipped)) ? Number(framesSkipped) : 1;
    return (prevBullets || [])
        .map((b) => {
            const dy = Number(b.dy) || (b.type === 'invader' ? 4 : -8);
            return {
                ...b,
                y: (Number(b.y) || 0) + dy * skip,
            };
        })
        .filter((b) => {
            const y = Number(b.y) || 0;
            return y > -24 && y < SI_CANVAS_H + 24;
        });
}

export function computeSpaceInvaderFrame(run = {}, step = 0) {
    const history = run.history || run.move_history || [];
    const init = run.init_state || {};
    const clamped = history.length
        ? Math.max(0, Math.min(step, Math.max(history.length - 1, 0)))
        : 0;
    const entry = history[clamped] || {};
    const prev = clamped > 0 ? (history[clamped - 1] || {}) : {};

    const playerX = entry.player_x
        ?? entry.player?.x
        ?? init.player_x
        ?? run.player_x
        ?? (SI_CANVAS_W / 2 - SI_PLAYER_SIZE / 2);
    const playerY = entry.player_y
        ?? entry.player?.y
        ?? init.player_y
        ?? (SI_CANVAS_H - 60);

    const invadersRaw = entry.invaders ?? entry.invader_grid ?? init.invaders ?? run.invaders;
    const invaders = invadersRaw == null ? siDefaultInvaderLayout() : normalizeSpaceInvaders(invadersRaw);
    const bunkers = normalizeSpaceInvaderBunkers(entry.bunkers ?? init.bunkers ?? run.bunkers);

    let bullets = collectSpaceInvaderBullets(entry);
    const prevInvaders = prev.invaders != null ? normalizeSpaceInvaders(prev.invaders) : [];
    bullets = bullets.concat(bulletsForVanishedInvaders(prevInvaders, invaders, bullets));
    if (!bullets.length) {
        const carried = interpolateSpaceInvaderBullets(collectSpaceInvaderBullets(prev), 1);
        bullets = carried;
    }

    return {
        canvasW: SI_CANVAS_W,
        canvasH: SI_CANVAS_H,
        playerX: Number(playerX) || 0,
        playerY: Number(playerY) || (SI_CANVAS_H - 60),
        playerSize: SI_PLAYER_SIZE,
        invaders,
        bunkers,
        bullets,
        score: entry.score ?? run.score ?? 0,
        lives: entry.lives ?? init.lives ?? run.lives ?? 3,
        level: entry.level ?? init.level ?? run.level ?? 1,
        ticks: clamped,
        finalScore: finalScore(run, entry.score ?? run.score ?? 0),
    };
}

export function computeReplayStats(run, gameKind, step) {
    const final = finalScore(run);
    switch (gameKind) {
        case 'snake': {
            const frame = computeSnakeFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'length', label: 'Length', value: frame.length },
                { key: 'grid', label: 'Grid', value: `${frame.boardSize}x${frame.boardSize}` },
                { key: 'ticks', label: 'Ticks', value: frame.ticks },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'puzzle': {
            const frame = computePuzzleFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'tiles', label: 'Tiles', value: frame.tilesPlaced },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'santa_drop': {
            const frame = computeSantaDropFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'landed', label: 'Landed', value: `${frame.landed}/${frame.dropped}` },
                { key: 'combo', label: 'Combo', value: frame.combo > 1 ? `x${frame.combo}` : null },
                { key: 'ticks', label: 'Tick', value: frame.tick },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'pong': {
            const frame = computePongFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: `${frame.score.player ?? 0}-${frame.score.opponent ?? 0}`, emphasis: true },
                { key: 'player-hits', label: 'Player hits', value: frame.playerHits },
                { key: 'opponent-hits', label: 'Opponent hits', value: frame.opponentHits },
                { key: 'goals', label: 'Goals', value: `${frame.goals.player ?? frame.score.player ?? 0}-${frame.goals.opponent ?? frame.score.opponent ?? 0}` },
                { key: 'final-score', label: 'Final', value: final },
            ]);
        }
        case 'road_fighter': {
            const frame = computeRoadFighterFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'level', label: 'Level', value: frame.level },
                { key: 'distance', label: 'Distance', value: Math.round(frame.distance) },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'two_joint_pole': {
            const frame = computeTwoJointPoleFrame(run, step);
            if (frame.multiplayer) {
                const stats = [
                    { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                    { key: 'p1', label: 'P1', value: Number(frame.scores?.player_1 ?? 0).toFixed(1) },
                    { key: 'p2', label: 'P2', value: Number(frame.scores?.player_2 ?? 0).toFixed(1) },
                ];
                if (frame.round_index != null) {
                    stats.push({
                        key: 'round',
                        label: 'Round',
                        value: frame.num_episodes != null
                            ? `${frame.round_index}/${frame.num_episodes}`
                            : frame.round_index,
                    });
                }
                if (frame.round_event === 'start') {
                    stats.push({ key: 'round-event', label: 'Event', value: 'Start' });
                } else if (frame.round_event === 'finish') {
                    stats.push({
                        key: 'round-event',
                        label: 'Result',
                        value: frame.round_result_label || 'Over',
                        emphasis: true,
                    });
                }
                stats.push({ key: 'moves', label: 'Moves', value: frame.step });
                stats.push({ key: 'final-score', label: 'Final', value: frame.finalScore });
                return makeStats(stats);
            }
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.step },
                { key: 'reward', label: 'Reward', value: frame.reward },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'ttt': {
            const frame = computeTttFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'outcome', label: 'Outcome', value: frame.outcome },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'c4': {
            const frame = computeC4Frame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'outcome', label: 'Outcome', value: frame.outcome },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'pac_man': {
            const frame = computePacManFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'lives', label: 'Lives', value: frame.lives },
                { key: 'ticks', label: 'Ticks', value: frame.ticks },
                { key: 'pellets', label: 'Pellets', value: frame.pellets.length + frame.power.length },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'battleship': {
            const frame = computeBattleshipFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'hits', label: 'Hits', value: frame.hits },
                { key: 'sunk', label: 'Sunk', value: frame.sunk },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'minesweeper': {
            const frame = computeMinesweeperFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'revealed', label: 'Revealed', value: frame.revealed },
                { key: 'flags', label: 'Flags', value: frame.flags },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'numberlink': {
            const frame = computeNumberlinkFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'filled', label: 'Filled', value: frame.filled },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'lights_out': {
            const frame = computeLightsOutFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'moves', label: 'Moves', value: frame.moves },
                { key: 'lights-on', label: 'Lights', value: frame.lightsOn },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'tetris': {
            const frame = computeTetrisFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'lines', label: 'Lines', value: frame.lines },
                { key: 'ticks', label: 'Ticks', value: frame.ticks },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'space_invader': {
            const frame = computeSpaceInvaderFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'lives', label: 'Lives', value: frame.lives },
                { key: 'level', label: 'Level', value: frame.level },
                { key: 'ticks', label: 'Ticks', value: frame.ticks },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'blackjack': {
            const frame = computeBlackjackFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'bankroll', label: 'Bankroll', value: frame.bankroll },
                { key: 'rounds', label: 'Rounds', value: frame.rounds },
                { key: 'outcome', label: 'Outcome', value: frame.outcome },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        case 'bandit': {
            const frame = computeBanditFrame(run, step);
            return makeStats([
                { key: 'score', label: 'Score', value: frame.score, emphasis: true },
                { key: 'spins', label: 'Spins', value: frame.spins },
                { key: 'balance', label: 'Balance', value: frame.balance },
                { key: 'final-score', label: 'Final', value: frame.finalScore },
            ]);
        }
        default: {
            const hist = timeline(run);
            const clamped = Math.max(0, Math.min(step, Math.max(hist.length - 1, 0)));
            const entry = hist[clamped];
            const score = stepScoreFromEntry(entry, null, run, final);
            return makeStats([
                { key: 'score', label: 'Score', value: score, emphasis: true },
                { key: 'moves', label: 'Moves', value: Math.max(0, Math.min(toCount(step), hist.length)) },
                { key: 'final-score', label: 'Final', value: final },
            ]);
        }
    }
}

export function SpaceInvaderReplayView({ run, step }) {
    const frame = useMemo(() => computeSpaceInvaderFrame(run, step), [run, step]);
    const player = useMemo(() => ({
        x: frame.playerX,
        y: frame.playerY,
        width: frame.playerSize,
        height: frame.playerSize,
    }), [frame.playerX, frame.playerY, frame.playerSize]);

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                <Chip
                    size="small"
                    label={`Score ${frame.score}`}
                    sx={{
                        backgroundColor: 'rgba(255,215,0,0.2)',
                        color: '#FFD700',
                        border: '1px solid rgba(255,215,0,0.3)',
                        fontWeight: 'bold',
                    }}
                />
                <Chip
                    size="small"
                    label={`Lives ${frame.lives}`}
                    sx={{
                        backgroundColor: 'rgba(0,255,0,0.2)',
                        color: '#00ff00',
                        border: '1px solid rgba(0,255,0,0.3)',
                    }}
                />
                <Chip
                    size="small"
                    label={`Level ${frame.level}`}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                    }}
                />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%', ...SI_PLAYFIELD_SX, overflow: 'hidden' }}>
                    <SpaceInvaderScene
                        data-testid="space-invader-replay-scene"
                        player={player}
                        invaders={frame.invaders}
                        bunkers={frame.bunkers}
                        bullets={frame.bullets}
                        level={frame.level || 1}
                    />
                </Box>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Generic history fallback (remaining games)
// ---------------------------------------------------------------------------

export function HistoryReplayView({ run, step }) {
    const history = timeline(run);
    const clamped = Math.max(0, Math.min(step, Math.max(history.length - 1, 0)));
    const entry = history[clamped];
    const preview = entry == null
        ? '—'
        : (typeof entry === 'object' ? JSON.stringify(entry, null, 2) : String(entry));

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: THEME.secondary, mb: 1 }}>
                Frame {clamped + 1} / {history.length}
            </Typography>
            <Box component="pre" sx={{
                textAlign: 'left', maxWidth: 480, mx: 'auto',
                bgcolor: 'rgba(0,0,0,0.35)', p: 2, borderRadius: 2,
                color: 'white', fontSize: '0.75rem', overflow: 'auto', maxHeight: 280,
                border: `1px solid ${THEME.border}`,
            }}>
                {preview}
            </Box>
        </Box>
    );
}

export function StaticFallbackView({ run }) {
    return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography sx={{ color: THEME.secondary, mb: 2 }}>
                No step-by-step replay data available for this run. Showing final result only.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {run.score !== undefined && (
                    <Chip label={`Score: ${run.score}`} sx={{ bgcolor: THEME.primary, color: 'black', fontWeight: 'bold' }} />
                )}
                {run.game_id && <Chip label={run.game_id} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />}
                {(run.agent_id || run.agent) && (
                    <Chip label={run.agent_id || run.agent} variant="outlined" sx={{ color: 'white', borderColor: THEME.border }} />
                )}
            </Stack>
        </Box>
    );
}
